import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Wrench, CheckCircle, AlertCircle, Truck, Clock, Award } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ProviderApplicationPage = () => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience_years: '',
    certifications: '',
    service_area: '',
    additional_info: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/roadside/apply-provider`,
        {
          ...formData,
          experience_years: parseInt(formData.experience_years)
        }
      );

      toast.success('Application submitted successfully!');
      setSubmitted(true);
    } catch (error) {
      toast.error('Application failed', {
        description: error.response?.data?.detail || 'Please try again'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Application Submitted!
            </h2>
            <p className="text-gray-600 mb-6">
              Thank you for your interest in joining Vargas Tire & Auto Repair as a service provider.
              We'll review your application and contact you within 2-3 business days.
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>What's Next?</strong><br />
                Our team will review your qualifications and experience. If approved, you'll receive a secure
                signup link via email to complete your registration.
              </p>
            </div>
            <Button onClick={() => window.location.href = '/'} className="bg-red-600 hover:bg-red-700">
              Return to Home
            </Button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Wrench className="w-16 h-16 text-red-600 mx-auto mb-4" />
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Join Our Provider Network
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Become a trusted service provider for Vargas Tire & Auto Repair and help us serve
            customers across Florida with quality mobile mechanic services.
          </p>
        </div>

        {/* Benefits */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          <Card className="p-6 text-center">
            <Truck className="w-10 h-10 text-red-600 mx-auto mb-3" />
            <h3 className="font-bold text-gray-900 mb-2">Flexible Schedule</h3>
            <p className="text-sm text-gray-600">
              Choose your availability and work on your own terms
            </p>
          </Card>
          <Card className="p-6 text-center">
            <Clock className="w-10 h-10 text-red-600 mx-auto mb-3" />
            <h3 className="font-bold text-gray-900 mb-2">24/7 Opportunities</h3>
            <p className="text-sm text-gray-600">
              Access to roadside assistance calls around the clock
            </p>
          </Card>
          <Card className="p-6 text-center">
            <Award className="w-10 h-10 text-red-600 mx-auto mb-3" />
            <h3 className="font-bold text-gray-900 mb-2">Competitive Pay</h3>
            <p className="text-sm text-gray-600">
              Fair compensation for quality service delivery
            </p>
          </Card>
        </div>

        {/* Application Form */}
        <Card className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Provider Application</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Information</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="john@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="(305) 123-4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Years of Experience <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="number"
                    name="experience_years"
                    value={formData.experience_years}
                    onChange={handleChange}
                    required
                    min="0"
                    max="50"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="5"
                  />
                </div>
              </div>
            </div>

            {/* Professional Details */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Professional Details</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Certifications & Licenses <span className="text-red-600">*</span>
                  </label>
                  <textarea
                    name="certifications"
                    value={formData.certifications}
                    onChange={handleChange}
                    required
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="ASE Certified, State License #12345, etc."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    List all relevant certifications, licenses, and professional qualifications
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Service Area <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="text"
                    name="service_area"
                    value={formData.service_area}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Miami-Dade County, Broward County, etc."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Specify the counties or cities you're available to serve
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Additional Information
                  </label>
                  <textarea
                    name="additional_info"
                    value={formData.additional_info}
                    onChange={handleChange}
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Tell us about your specialties, availability, equipment, or anything else we should know..."
                  />
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-700">
                  <strong>Important:</strong> By submitting this application, you confirm that all information
                  provided is accurate and complete. Background checks and verification of certifications will
                  be conducted during the review process.
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-4">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white py-3 text-lg"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => window.location.href = '/'}
                className="px-8"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};
